// The console's WebSocket transport to the realtime gateway (spec/09 §1–§3, §6.5, §8, §9).
//
// One socket per organisation, shared by every venue screen that wants live push (Floor, Kitchen,
// Sessions). REST still changes state; this only carries the picture. The client never sends an
// `evt` or a `cmd` — the gateway refuses those from a console (spec/09 §6.6). It:
//
//   auth       opens the socket, then sends `auth.connect` with a realtime token in the first frame,
//              never in the URL (spec/09 §1). 60 s before the token expires the gateway sends
//              `auth.expiring`; the client fetches a fresh token and sends `auth.refresh`.
//   subscribe  a screen subscribes to a venue channel; the client sends `channel.subscribe` and
//              fans the channel's `evt` frames out to that screen's listener. On every reconnect it
//              subscribes again — there is no server-side replay, the snapshot is the recovery
//              (spec/09 §4), so a screen resyncs from REST when the socket comes back.
//   keepalive  the client sends its own `realtime.ping` and treats a long silence as a dead socket
//              (spec/09 §1), closing it so the reconnect path runs.
//   reconnect  exponential backoff with full jitter, first window 0–2 s for a console, capped at 60 s
//              (spec/09 §8); `server.draining`'s `reconnectAfterMs` is added on top of the jitter.
//   close      4403 (banned / removed) signs the console out and does not reconnect; every other
//              close reconnects with backoff and the screens resync from their snapshots (spec/09 §9).
//
// Transport-agnostic: it is handed a socket factory (a real `WebSocket` in the browser, a fake in a
// test) and a way to fetch a token, so nothing here reaches for a global that a test cannot replace.

import {
  ACCESS_CHANGED,
  ACCESS_REVOKED,
  AUTH_CONNECT,
  AUTH_EXPIRING,
  AUTH_REFRESH,
  CHANNEL_SUBSCRIBE,
  CHANNEL_UNSUBSCRIBE,
  REALTIME_CLOSE_CODES,
  REALTIME_PING,
  SERVER_DRAINING,
} from '@clavian/contracts/constants';
import type { Channel, RealtimeEnvelope, ServerDrainingPayload } from '@clavian/contracts/realtime';
import { uuidToTypeId } from '@clavian/contracts/typeid';

/** Where a screen stands with the gateway. `offline` covers "not started", "backing off" and "down". */
export type RealtimeStatus = 'connecting' | 'live' | 'offline';

/** A realtime token, as `POST /v1/realtime/token` returns the parts this client needs. */
export interface RealtimeToken {
  readonly token: string;
  readonly expiresAt: string;
}

/** A listener for one channel's events, or for the connection's status. */
export type ChannelListener = (envelope: RealtimeEnvelope) => void;
export type StatusListener = (status: RealtimeStatus) => void;

/** The minimal socket the client drives; a browser `WebSocket` is adapted to it by the default factory. */
export interface RealtimeSocket {
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface RealtimeSocketHandlers {
  readonly onOpen: () => void;
  readonly onMessage: (data: string) => void;
  readonly onClose: (code: number) => void;
  readonly onError: () => void;
}

export type RealtimeSocketFactory = (url: string, handlers: RealtimeSocketHandlers) => RealtimeSocket;

export interface RealtimeConnectionOptions {
  /** The gateway URL, e.g. `wss://rt.clavian.io/v1/ws`. No query string (spec/09 §1). */
  readonly url: string;
  /** Fetch a realtime token for this principal — the member token for the organisation. */
  readonly fetchToken: () => Promise<RealtimeToken>;
  /** Banned or removed (4403 / `access.revoked`): sign the console out; never reconnected. */
  readonly onRevoked?: () => void;
  /** `access.changed`: refetch `/v1/me/access` (spec/09 §3). */
  readonly onAccessChanged?: () => void;
  /**
   * An event on a channel nobody subscribed to — the principal's own, which the gateway joins at auth (spec/09 §3).
   * A countdown screen (ADR-0033) does not know its station's id, so it hears its `display.changed` here.
   */
  readonly onOwnEvent?: ChannelListener;
  /** For a console's own diagnostics; never a token or a channel's data. */
  readonly onError?: (message: string, error?: unknown) => void;
  /** A `WebSocket` in the browser; a fake in a test. */
  readonly createSocket?: RealtimeSocketFactory;
  readonly now?: () => number;
  readonly random?: () => number;
}

/** spec/09's numbers for a console's half of the socket. */
const PING_INTERVAL_MS = 15_000;
/** No frame from the gateway for this long means the socket is dead (spec/09 §1: 15 s is "offline"). */
const STALE_AFTER_MS = 30_000;
/** The first reconnect window: full jitter over 0–2 s for a console (spec/09 §8). */
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_CAP_MS = 60_000;
/** Refetch the token this long before it expires, if `auth.expiring` has not already asked. */
const REFRESH_LEAD_MS = 60_000;

function browserSocketFactory(url: string, handlers: RealtimeSocketHandlers): RealtimeSocket {
  const socket = new WebSocket(url);
  socket.onopen = () => handlers.onOpen();
  socket.onmessage = (event: MessageEvent) => {
    if (typeof event.data === 'string') handlers.onMessage(event.data);
  };
  socket.onclose = (event: CloseEvent) => handlers.onClose(event.code);
  socket.onerror = () => handlers.onError();
  return {
    send: (data) => socket.send(data),
    close: (code, reason) => socket.close(code, reason),
  };
}

export interface RealtimeConnection {
  /** Open the socket (idempotent). */
  start(): void;
  /** Close the socket and stop reconnecting (idempotent). */
  stop(): void;
  /**
   * Try again now, rather than when the backoff says: a member pressed "Retry" on a screen that has been without live
   * updates for a while. A no-op when the socket is live or the connection was never started; otherwise the pending
   * backoff is dropped, the next window starts from the first again, and a fresh token and socket are made at once.
   */
  retry(): void;
  /** Subscribe a listener to a channel; returns an unsubscribe. Safe before the socket is live. */
  subscribe(channel: Channel, listener: ChannelListener): () => void;
  /** Watch the connection status; returns an unsubscribe. Called at once with the current status. */
  watch(listener: StatusListener): () => void;
  readonly status: RealtimeStatus;
}

interface OpenSocket {
  /** Filled the moment `createSocket` returns, before any real `onOpen`. */
  socket: RealtimeSocket;
  /** The `id` of the `auth.connect` frame, to recognise its `ok`. */
  authId: string;
  /** True once the gateway has accepted the token. */
  authed: boolean;
}

export function createRealtimeConnection(options: RealtimeConnectionOptions): RealtimeConnection {
  const createSocket = options.createSocket ?? browserSocketFactory;
  const now = options.now ?? (() => Date.now());
  const random = options.random ?? (() => Math.random());
  const report = (message: string, error?: unknown) => options.onError?.(message, error);

  const channelListeners = new Map<Channel, Set<ChannelListener>>();
  const statusListeners = new Set<StatusListener>();

  let desired = false;
  let status: RealtimeStatus = 'offline';
  let open: OpenSocket | undefined;
  let attempt = 0;
  /** A `server.draining` delay to add to the next backoff, once (spec/09 §8). */
  let drainDelayMs = 0;
  let expiresAt = 0;
  let lastFrameAt = 0;
  /** Guards against a stale async token fetch resolving after a newer connect started. */
  let generation = 0;

  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;
  let keepaliveTimer: ReturnType<typeof setInterval> | undefined;

  function setStatus(next: RealtimeStatus): void {
    if (status === next) return;
    status = next;
    for (const listener of [...statusListeners]) listener(next);
  }

  function newId(): string {
    return uuidToTypeId('msg', crypto.randomUUID());
  }

  function sendFrame(frame: Omit<RealtimeEnvelope, 'id' | 'ts'> & { id?: string }): string {
    const id = frame.id ?? newId();
    const envelope: RealtimeEnvelope = { ...frame, id, ts: new Date(now()).toISOString() };
    open?.socket.send(JSON.stringify(envelope));
    return id;
  }

  // -------------------------------------------------------------------------------------------
  // Timers
  // -------------------------------------------------------------------------------------------

  function clearTimers(): void {
    if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
    if (refreshTimer !== undefined) clearTimeout(refreshTimer);
    if (keepaliveTimer !== undefined) clearInterval(keepaliveTimer);
    reconnectTimer = undefined;
    refreshTimer = undefined;
    keepaliveTimer = undefined;
  }

  function startKeepalive(): void {
    if (keepaliveTimer !== undefined) clearInterval(keepaliveTimer);
    keepaliveTimer = setInterval(() => {
      if (open === undefined) return;
      if (now() - lastFrameAt > STALE_AFTER_MS) {
        // Silence past the offline threshold: the socket is gone. Close it so onClose reconnects.
        report('realtime socket went silent; reconnecting');
        teardownSocket(REALTIME_CLOSE_CODES.normal);
        scheduleReconnect();
        return;
      }
      if (open.authed) sendFrame({ k: 'ping', type: REALTIME_PING });
    }, PING_INTERVAL_MS);
  }

  function scheduleTokenRefresh(): void {
    if (refreshTimer !== undefined) clearTimeout(refreshTimer);
    const lead = expiresAt - now() - REFRESH_LEAD_MS;
    refreshTimer = setTimeout(() => void refreshToken(), Math.max(0, lead));
  }

  // -------------------------------------------------------------------------------------------
  // Connecting
  // -------------------------------------------------------------------------------------------

  function connect(): void {
    if (!desired || open !== undefined) return;
    setStatus('connecting');
    const mine = ++generation;
    void options
      .fetchToken()
      .then((token) => {
        if (!desired || mine !== generation) return;
        openSocket(token);
      })
      .catch((error: unknown) => {
        if (!desired || mine !== generation) return;
        report('could not get a realtime token', error);
        scheduleReconnect();
      });
  }

  function openSocket(token: RealtimeToken): void {
    expiresAt = Date.parse(token.expiresAt) || now() + REFRESH_LEAD_MS;
    const mine = generation;
    // The record exists before the socket, so a socket that opens synchronously (a test fake) still
    // finds it. `socket` is filled in the moment `createSocket` returns, before any real `onOpen`.
    const record: OpenSocket = { socket: undefined as unknown as RealtimeSocket, authId: '', authed: false };
    open = record;
    lastFrameAt = now();
    record.socket = createSocket(options.url, {
      onOpen: () => {
        if (open !== record || mine !== generation) return;
        record.authId = sendFrame({ k: 'auth', type: AUTH_CONNECT, p: { token: token.token } });
      },
      onMessage: (data) => {
        if (open === record && mine === generation) onMessage(data);
      },
      onClose: (code) => {
        if (mine !== generation) return;
        onClose(code);
      },
      onError: () => {
        if (mine !== generation) return;
        report('realtime socket error');
        // A browser fires `close` after `error`; onClose drives the reconnect.
      },
    });
  }

  function teardownSocket(code: number): void {
    if (open === undefined) return;
    const socket = open.socket;
    open = undefined;
    // A torn-down socket has no use for its keepalive or its token refresh.
    if (refreshTimer !== undefined) clearTimeout(refreshTimer);
    if (keepaliveTimer !== undefined) clearInterval(keepaliveTimer);
    refreshTimer = undefined;
    keepaliveTimer = undefined;
    // A new generation so a late frame from this socket is ignored.
    generation += 1;
    try {
      socket.close(code, '');
    } catch (error) {
      report('closing a realtime socket threw', error);
    }
  }

  function scheduleReconnect(): void {
    setStatus('offline');
    if (!desired) return;
    if (reconnectTimer !== undefined) return;
    const window = Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** Math.min(attempt, 5));
    const delay = random() * window + drainDelayMs;
    drainDelayMs = 0;
    attempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = undefined;
      connect();
    }, delay);
  }

  // -------------------------------------------------------------------------------------------
  // Frames from the gateway
  // -------------------------------------------------------------------------------------------

  function onMessage(data: string): void {
    lastFrameAt = now();
    let envelope: RealtimeEnvelope | undefined;
    try {
      envelope = JSON.parse(data) as RealtimeEnvelope;
    } catch {
      return;
    }
    if (open === undefined || typeof envelope.k !== 'string') return;

    switch (envelope.k) {
      case 'ok':
        if (!open.authed && envelope.re === open.authId) onAuthenticated();
        return;
      case 'err':
        if (!open.authed) {
          // The token was refused before auth completed: get a new one and try again.
          report(`realtime auth refused: ${messageOf(envelope)}`);
          teardownSocket(REALTIME_CLOSE_CODES.unauthenticated);
          scheduleReconnect();
        } else {
          report(`realtime refused a frame: ${messageOf(envelope)}`);
        }
        return;
      case 'evt':
        onEvent(envelope);
        return;
      default:
        return;
    }
  }

  function onAuthenticated(): void {
    if (open === undefined) return;
    open.authed = true;
    attempt = 0;
    setStatus('live');
    // No server-side replay for consoles: re-subscribe every held channel; the screens resync from
    // their snapshots (spec/09 §4).
    for (const channel of channelListeners.keys()) sendFrame({ k: 'sub', type: CHANNEL_SUBSCRIBE, ch: channel });
    scheduleTokenRefresh();
    startKeepalive();
  }

  function onEvent(envelope: RealtimeEnvelope): void {
    switch (envelope.type) {
      case AUTH_EXPIRING:
        void refreshToken();
        return;
      case ACCESS_CHANGED:
        options.onAccessChanged?.();
        return;
      case ACCESS_REVOKED:
        // Banned or removed: the gateway closes with 4403 next; onClose signs out and does not reconnect.
        return;
      case SERVER_DRAINING: {
        const payload = envelope.p as ServerDrainingPayload | undefined;
        const suggested = typeof payload?.reconnectAfterMs === 'number' ? payload.reconnectAfterMs : 0;
        drainDelayMs = Math.max(0, Math.min(RECONNECT_CAP_MS, suggested));
        return;
      }
      default:
        break;
    }
    const channel = envelope.ch;
    if (channel === undefined) return;
    const listeners = channelListeners.get(channel);
    if (listeners === undefined) {
      // The principal's own channel, which the gateway joins at auth and nobody subscribes to (a countdown screen's
      // `display:{stationId}`, ADR-0033): handed to `onOwnEvent`, when the caller listens for it.
      try {
        options.onOwnEvent?.(envelope);
      } catch (error) {
        report('a realtime own-channel listener threw', error);
      }
      return;
    }
    for (const listener of [...listeners]) {
      try {
        listener(envelope);
      } catch (error) {
        report('a realtime channel listener threw', error);
      }
    }
  }

  function onClose(code: number): void {
    open = undefined;
    if (refreshTimer !== undefined) clearTimeout(refreshTimer);
    if (keepaliveTimer !== undefined) clearInterval(keepaliveTimer);
    refreshTimer = undefined;
    keepaliveTimer = undefined;
    if (!desired) {
      setStatus('offline');
      return;
    }
    if (code === REALTIME_CLOSE_CODES.forbidden) {
      // Banned, removed, or the sign-in no longer works: sign out, do not reconnect (spec/09 §9).
      desired = false;
      setStatus('offline');
      options.onRevoked?.();
      return;
    }
    scheduleReconnect();
  }

  async function refreshToken(): Promise<void> {
    if (open === undefined || !open.authed) return;
    const mine = generation;
    try {
      const token = await options.fetchToken();
      if (open === undefined || mine !== generation) return;
      expiresAt = Date.parse(token.expiresAt) || now() + REFRESH_LEAD_MS;
      sendFrame({ k: 'auth', type: AUTH_REFRESH, p: { token: token.token } });
      scheduleTokenRefresh();
    } catch (error) {
      if (mine !== generation) return;
      report('could not refresh the realtime token', error);
      // Let the token lapse: the gateway closes with 4401 and the reconnect path fetches a fresh one.
    }
  }

  // -------------------------------------------------------------------------------------------
  // Public surface
  // -------------------------------------------------------------------------------------------

  return {
    get status() {
      return status;
    },

    start() {
      if (desired) return;
      desired = true;
      connect();
    },

    stop() {
      if (!desired && open === undefined) return;
      desired = false;
      clearTimers();
      teardownSocket(REALTIME_CLOSE_CODES.normal);
      setStatus('offline');
    },

    retry() {
      if (!desired || status === 'live') return;
      if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
      attempt = 0;
      // A socket still opening is dropped for a new one; a token still in flight is made stale by `connect`'s new
      // generation, so only the fresh attempt opens a socket.
      teardownSocket(REALTIME_CLOSE_CODES.normal);
      connect();
    },

    subscribe(channel, listener) {
      let listeners = channelListeners.get(channel);
      const isFirst = listeners === undefined;
      if (listeners === undefined) {
        listeners = new Set();
        channelListeners.set(channel, listeners);
      }
      listeners.add(listener);
      if (isFirst && open?.authed === true) {
        sendFrame({ k: 'sub', type: CHANNEL_SUBSCRIBE, ch: channel });
      }
      return () => {
        const held = channelListeners.get(channel);
        if (held === undefined) return;
        held.delete(listener);
        if (held.size > 0) return;
        channelListeners.delete(channel);
        if (open?.authed === true) sendFrame({ k: 'unsub', type: CHANNEL_UNSUBSCRIBE, ch: channel });
      };
    },

    watch(listener) {
      statusListeners.add(listener);
      listener(status);
      return () => {
        statusListeners.delete(listener);
      };
    },
  };
}

function messageOf(envelope: RealtimeEnvelope): string {
  const payload = envelope.p as { message?: unknown; code?: unknown } | undefined;
  const code = typeof payload?.code === 'string' ? payload.code : 'error';
  const message = typeof payload?.message === 'string' ? payload.message : '';
  return message === '' ? code : `${code}: ${message}`;
}
